import { DndContext } from '@dnd-kit/core';
import React, { useState } from 'react';
import { TaskCard } from 'shared/components/connected/task-card';
import { OverlayComponentWrapper } from 'shared/components/ui/overlay-component-wrapper';
import { SortableOverlay } from 'shared/components/ui/sortable-overlay';
import { useSortableItemsControls } from 'shared/hooks/use-sortable-items-controls';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { ID } from 'shared/types/id';

import { TasksList } from './tasks-list';

export type SelfSortingTasksListProps = Omit<
  React.ComponentProps<typeof TasksList>,
  'onClick' | 'onUnfocus' | 'noHover' | 'canReorder'
> & {
  onReorder?: (ids: ID[]) => void;
  hasOwnSelectedState?: boolean;
};

export const SelfSortingTasksList: React.FC<SelfSortingTasksListProps> = ({
  tasks,
  onReorder,
  hasOwnSelectedState,
  selectedTask,
  ...rest
}) => {
  const [activeTask, setActiveTask] = useState<ID>();
  const {
    items,
    activeItem,
    handleDragStart,
    handleDragCancel,
    handleDragEnd,
  } = useSortableItemsControls({ items: tasks, onDragEnd: onReorder });
  const sensors = useSortableSensors();

  const clearSelectedTask = () => setActiveTask(undefined);

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
    >
      <TasksList
        tasks={items}
        onClick={setActiveTask}
        onUnfocus={clearSelectedTask}
        selectedTask={hasOwnSelectedState ? activeTask : selectedTask}
        canReorder={!!onReorder}
        {...rest}
      />

      <SortableOverlay>
        {!!activeItem && (
          <OverlayComponentWrapper>
            <TaskCard
              task={activeItem}
              onUnfocus={clearSelectedTask}
              onClick={setActiveTask}
              isSelected={
                hasOwnSelectedState
                  ? activeTask === activeItem.id
                  : selectedTask === activeItem.id
              }
              {...rest}
            />
          </OverlayComponentWrapper>
        )}
      </SortableOverlay>
    </DndContext>
  );
};
